import { cloneElement, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus, UserCircle, Key, ToggleRight, ToggleLeft, Users as UsersIcon,
    UsersThree, LinkSimple, LinkBreak,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useAuth, ROLE_LABELS } from "../contexts/AuthContext";
import { rolePersonaLabel } from "../lib/rolePersona";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const LEGACY_ROLES = ["super_admin", "accounts", "sales", "viewer", "data_user", "logo_user"];

function buildEmployeeIndex(items) {
    // Map linked_user_id → employee summary for O(1) display in the Users table.
    const out = {};
    for (const e of items || []) {
        if (e.linked_user_id) out[e.linked_user_id] = e;
    }
    return out;
}

function NewUserDialog({ onCreated, availableRoles }) {
    const blank = { email: "", name: "", role: "viewer", password: "", phone: "" };
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState(blank);
    const baselineRef = useRef(JSON.stringify(blank));

    useEffect(() => {
        if (open) {
            setForm(blank);
            baselineRef.current = JSON.stringify(blank);
        }
        // eslint-disable-next-line
    }, [open]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if ((form.phone || "").trim().length < 4) {
            toast.error("Contact phone is required (used on every proposal as Submitted By)");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/users", form);
            toast.success("User created");
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            setOpen(false);
            setForm(blank);
            onCreated?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create user"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <>
            <Button
                data-testid="new-user-btn"
                onClick={() => setOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
            >
                <Plus size={16} weight="bold" /> New user
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                testId="new-user-dialog"
                title="Add a user"
                description="User receives access immediately. Share credentials securely."
            >
                <form onSubmit={submit} className="space-y-3" data-testid="user-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Name</Label>
                            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="mt-1.5" data-testid="user-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Role</Label>
                            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="user-role-select">
                                {(availableRoles && availableRoles.length > 0 ? availableRoles : LEGACY_ROLES.map((c) => ({ code: c, name: ROLE_LABELS[c] || c }))).map((r) => (
                                    <option key={r.code} value={r.code}>{r.name || ROLE_LABELS[r.code] || r.code}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Email</Label>
                        <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} placeholder="you@reedos.in" className="mt-1.5" data-testid="user-email-input" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Contact phone</Label>
                        <Input
                            required
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="+91 70219 12083"
                            className="mt-1.5"
                            data-testid="user-phone-input"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">Shown on every proposal raised by this user as &ldquo;Submitted By&rdquo;.</p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Temporary password</Label>
                        <Input required type="text" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 8 characters" className="mt-1.5 font-mono" data-testid="user-password-input" />
                        <p className="text-[11px] text-slate-500 mt-1">Share securely; user cannot change it themselves yet.</p>
                    </div>
                    <DialogFooter>
                        <FormDialogCancel testId="user-cancel-btn">Cancel</FormDialogCancel>
                        <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="user-submit-btn">
                            {submitting ? "Creating..." : "Create user"}
                        </Button>
                    </DialogFooter>
                </form>
            </FormDialog>
        </>
    );
}

function EditRoleDialog({ user, onUpdated, children, availableRoles }) {
    const [open, setOpen] = useState(false);
    const [role, setRole] = useState(user.role);
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef(user.role);

    useEffect(() => {
        if (open) {
            setRole(user.role);
            baselineRef.current = user.role;
        }
    }, [open, user.role]);

    const isDirty = role !== baselineRef.current;

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.patch(`/users/${user.id}`, { role });
            toast.success("Role updated");
            baselineRef.current = role;  // bypass discard prompt
            setOpen(false);
            onUpdated?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to update role"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <>
            {cloneElement(children, { onClick: () => setOpen(true) })}
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                testId={`edit-role-dialog-${user.id}`}
                title="Change role"
            >
                <div>
                    <Label className="text-xs font-semibold">{user.name} · {user.email}</Label>
                    <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid={`edit-role-select-${user.id}`}>
                        {(availableRoles && availableRoles.length > 0 ? availableRoles : LEGACY_ROLES.map((c) => ({ code: c, name: ROLE_LABELS[c] || c }))).map((r) => (
                            <option key={r.code} value={r.code}>{r.name || ROLE_LABELS[r.code] || r.code}</option>
                        ))}
                    </select>
                </div>
                <DialogFooter>
                    <FormDialogCancel testId={`edit-role-cancel-${user.id}`}>Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`edit-role-submit-${user.id}`}>
                        {submitting ? "Saving..." : "Save"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

function ResetPasswordDialog({ user, children }) {
    const [open, setOpen] = useState(false);
    const [pwd, setPwd] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef("");

    useEffect(() => {
        if (open) {
            setPwd("");
            baselineRef.current = "";
        }
    }, [open]);

    const isDirty = pwd !== baselineRef.current;

    const submit = async () => {
        if (pwd.length < 8) { toast.error("Min 8 characters"); return; }
        setSubmitting(true);
        try {
            await api.post(`/users/${user.id}/reset-password`, { new_password: pwd });
            toast.success("Password reset");
            baselineRef.current = pwd;  // bypass discard prompt
            setOpen(false);
            setPwd("");
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <>
            {cloneElement(children, { onClick: () => setOpen(true) })}
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                testId={`reset-pwd-dialog-${user.id}`}
                title="Reset password"
                description={`New password for ${user.name} (${user.email}). Share it securely.`}
            >
                <Input type="text" minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Minimum 8 characters" className="font-mono" data-testid={`reset-pwd-input-${user.id}`} />
                <DialogFooter>
                    <FormDialogCancel testId={`reset-pwd-cancel-${user.id}`}>Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`reset-pwd-submit-${user.id}`}>
                        {submitting ? "Saving..." : "Reset password"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

export default function Users() {
    const { user: me, can } = useAuth();
    const [items, setItems] = useState([]);
    const [employeesByUserId, setEmployeesByUserId] = useState({});
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [usersResp, empResp, rolesResp] = await Promise.all([
                api.get("/users"),
                // Pull all employees so we can show the linked-employee badge.
                // Fallback to empty if the HR module is not accessible to the caller.
                api.get("/hr/employee/list?limit=1000").catch(() => ({ data: { items: [] } })),
                // Pull the live roles list so custom roles (developer, hr_user, etc.)
                // are available in the New/Edit role dropdowns. Phase E7 Phase C.
                api.get("/admin/role/list").catch(() => ({ data: { items: [] } })),
            ]);
            setItems(usersResp.data.items || []);
            setEmployeesByUserId(buildEmployeeIndex(empResp.data.items));
            setAvailableRoles((rolesResp.data.items || []).filter((r) => r.is_active));
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load users"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const toggleActive = async (u) => {
        try {
            await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
            toast.success(u.is_active ? "User deactivated" : "User activated");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    const roleLabel = (code) => {
        const r = availableRoles.find((x) => x.code === code);
        return (r && r.name) || ROLE_LABELS[code] || code;
    };

    // UAT RBAC-UX-05 (2026-07-14) — Business Persona derived from the
    // assigned role. Shown alongside the role name so operators can
    // quickly read the human-language role summary (e.g. "HR
    // Administrator") without recognising the technical role code.
    const personaLabel = (code) => {
        const r = availableRoles.find((x) => x.code === code);
        return rolePersonaLabel(code, r);
    };

    if (!can("user:write")) {
        return (
            <div className="p-8">
                <EmptyState
                    icon={<UsersIcon size={40} weight="thin" />}
                    title="Only Super Admins manage users"
                    description="Your role does not have access to this page."
                />
            </div>
        );
    }

    return (
        <div data-testid="users-page">
            <PageHeader
                eyebrow="Administration"
                title="Users & access"
                description="Only Super Admins can create, deactivate, or change the role of a user. Department / Designation / Reporting Manager come from the linked Employee record — they are never maintained on the User itself."
                actions={
                    <div className="flex items-center gap-2">
                        {can("employee:onboard") && (
                            <Link to="/admin/onboard">
                                <Button
                                    variant="outline"
                                    className="gap-1.5"
                                    data-testid="onboard-employee-user-cta"
                                >
                                    <UsersThree size={16} weight="bold" /> Onboard Employee / User
                                </Button>
                            </Link>
                        )}
                        <NewUserDialog onCreated={load} availableRoles={availableRoles} />
                    </div>
                }
            />
            <div className="px-6 sm:px-8 py-8">
                {loading ? (
                    <div className="text-sm text-slate-500">Loading users...</div>
                ) : items.length === 0 ? (
                    <EmptyState icon={<UsersIcon size={40} weight="thin" />} title="No users yet" description="Add the first one to get started." />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="users-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">User</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Role</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Linked employee</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Status</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Last login</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Added</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((u) => (
                                    <tr key={u.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`user-row-${u.id}`}>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-2.5">
                                                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                                                    <UserCircle size={20} weight="regular" />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-slate-900">{u.name}</div>
                                                    <div className="text-xs text-slate-500">{u.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-slate-700" data-testid={`user-role-persona-${u.id}`}>
                                            <div className="font-medium text-slate-800">{roleLabel(u.role)}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">{personaLabel(u.role)}</div>
                                        </td>
                                        <td className="px-5 py-3" data-testid={`user-linked-employee-${u.id}`}>
                                            {employeesByUserId[u.id] ? (
                                                <div className="text-xs">
                                                    <div className="font-medium text-slate-800">{employeesByUserId[u.id].full_name}</div>
                                                    <div className="text-slate-500 font-mono">{employeesByUserId[u.id].employee_code}</div>
                                                    <div className="text-slate-500">
                                                        {employeesByUserId[u.id].department || "—"}
                                                        {employeesByUserId[u.id].designation ? ` · ${employeesByUserId[u.id].designation}` : ""}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                                    <LinkBreak size={12} /> Not linked
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <StatusBadge status={u.is_active ? "Active" : "Draft"} data-testid={`user-status-${u.id}`} className={u.is_active ? "" : "!bg-rose-50 !text-rose-700 !border-rose-200"}>
                                                {u.is_active ? "Active" : "Deactivated"}
                                            </StatusBadge>
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-500">{u.last_login_at ? formatDateTime(u.last_login_at) : "Never"}</td>
                                        <td className="px-5 py-3 text-xs text-slate-500">{formatDateTime(u.created_at)}</td>
                                        <td className="px-5 py-3 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <EditRoleDialog user={u} onUpdated={load} availableRoles={availableRoles}>
                                                    <Button variant="ghost" size="sm" className="gap-1 text-slate-600 hover:text-slate-900" data-testid={`user-edit-role-${u.id}`}>
                                                        Role
                                                    </Button>
                                                </EditRoleDialog>
                                                <ResetPasswordDialog user={u}>
                                                    <Button variant="ghost" size="sm" className="gap-1 text-slate-600 hover:text-slate-900" data-testid={`user-reset-pwd-${u.id}`}>
                                                        <Key size={14} /> Reset pwd
                                                    </Button>
                                                </ResetPasswordDialog>
                                                {u.id !== me?.id && (
                                                    <Button
                                                        variant="ghost" size="sm"
                                                        className={u.is_active ? "text-rose-600 hover:bg-rose-50 gap-1" : "text-emerald-700 hover:bg-emerald-50 gap-1"}
                                                        onClick={() => toggleActive(u)}
                                                        data-testid={`user-toggle-active-${u.id}`}
                                                    >
                                                        {u.is_active ? (<><ToggleRight size={16} weight="fill" /> Deactivate</>) : (<><ToggleLeft size={16} /> Activate</>)}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
